import type { QualifiedName } from './types';

export const collapseRepeatedSegments = (raw: string): string => {
  if (!raw) {
    return raw;
  }
  const pattern = /\.([A-Za-z0-9_-]+)\.\1\b/g;
  let current = raw;
  let next = raw;
  do {
    current = next;
    next = current.replace(pattern, '.$1');
  } while (next !== current);
  return next;
};

export const normaliseQualifiedValue = (raw: string): string => collapseRepeatedSegments(raw.trim());

export const normaliseDisplayLabel = (raw?: string | null): string | undefined => {
  if (typeof raw !== 'string') {
    return undefined;
  }
  const trimmed = raw.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

export const parseQualifiedName = (raw: string, fallbackTable = 'global'): QualifiedName => {
  if (!raw) {
    return { table: fallbackTable, field: raw ?? '', qualified: `${fallbackTable}.` };
  }

  const trimmed = normaliseQualifiedValue(raw);
  const lastDot = trimmed.lastIndexOf('.');

  if (lastDot === -1) {
    return { table: fallbackTable, field: trimmed, qualified: `${fallbackTable}.${trimmed}` };
  }

  const tablePart = trimmed.slice(0, lastDot).trim() || fallbackTable;
  const fieldPart = trimmed.slice(lastDot + 1).trim();

  return {
    table: tablePart,
    field: fieldPart,
    qualified: `${tablePart}.${fieldPart}`,
  };
};

export const buildQualified = (table: string | undefined, field: string | undefined, fallbackTable: string): string => {
  const safeTable = (table && table.trim().length > 0 ? table.trim() : fallbackTable).trim();
  const safeField = (field && field.trim().length > 0 ? field.trim() : '').trim();
  const combined = safeField ? `${safeTable}.${safeField}` : safeTable;
  return normaliseQualifiedValue(combined);
};

export const deriveGroupLabel = (raw: string, fallback: string): string => {
  const trimmed = raw.trim();
  if (!trimmed) {
    return fallback;
  }
  const withoutPath = trimmed.split(/[\\/]/).filter(Boolean).pop() ?? trimmed;
  const withoutExtension = withoutPath.replace(/\.(csv|tsv|json|parquet|txt)$/i, '');
  const collapsed = withoutExtension.replace(/[_-]+/g, ' ').trim();
  if (!collapsed) {
    return withoutExtension || fallback;
  }
  return collapsed.replace(/\b\w/g, (char) => char.toUpperCase());
};
